/**
 * Merge-candidate review API client (task #58).
 *
 * Wraps the assayer-backend admin proxy which itself forwards to Cynapse.
 * Same shape for contacts + companies — different endpoints, identical
 * row schema.
 */
import { apiClient } from './config.js';

export type MergeReviewStatus =
  | 'pending'
  | 'approved_merge'
  | 'rejected_separate'
  | 'expired'
  | 'superseded'  // duplicate global_entity absorbed via a sibling merge in the same batch
  | 'approved_link';  // cross_type: contact linked to its person (same_person_as), nothing deleted (2026-09-22)

export interface ContactMergeCandidateRow {
  candidate_id: string;
  incoming_full_name: string;
  incoming_payload: Record<string, unknown>;
  source_announcement_id: string | null;
  matched_contact_id: string;
  matched_full_name: string | null;
  /** 'person' | 'contact' — the contacts tab executes two different merges (2026-09-20). */
  matched_entity_type?: 'person' | 'contact' | null;
  similarity_score: number;
  match_method: string;
  review_status: MergeReviewStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
}

export interface CompanyMergeCandidateRow {
  candidate_id: string;
  incoming_company_name: string;
  incoming_payload: Record<string, unknown>;
  matched_company_id: string;
  matched_company_name: string | null;
  similarity_score: number;
  match_method: string;
  review_status: MergeReviewStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
}

export interface FundMergeCandidateRow {
  candidate_id: string;
  incoming_fund_name: string;
  incoming_payload: Record<string, unknown>;
  matched_fund_id: string;
  matched_fund_name: string | null;
  similarity_score: number;
  match_method: string;
  review_status: MergeReviewStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
}

/** One page of a merge queue plus its real size (2026-09-22). */
export interface MergeCandidatePage<T> {
  items: T[];
  total: number;
  limit: number;
  offset: number;
}

export interface MergeListParams {
  limit?: number;
  offset?: number;
  /** Name contains, either side of the pair (not attribute changes). */
  q?: string;
}

function pageParams(status: string, p: MergeListParams) {
  const params: Record<string, string | number> = { status, limit: p.limit ?? 50, offset: p.offset ?? 0 };
  if (p.q && p.q.trim()) params.q = p.q.trim();
  return params;
}

export interface MergeActionResult {
  candidate_id: string;
  merged?: boolean;
  rejected?: boolean;
  /** cross_type approve: the contact was linked to the person; nothing was merged or deleted. */
  linked?: boolean;
  /** Set when an approve found the duplicate already absorbed by a sibling merge in the same batch. Not a failure — candidate is stamped 'superseded'. */
  superseded?: boolean;
  canonical_entity_id?: string;
  duplicate_entity_id?: string;
  actions?: Record<string, unknown>;
}

export async function listContactMergeCandidates(
  status: MergeReviewStatus | 'all' = 'pending',
  params: MergeListParams = {},
): Promise<MergeCandidatePage<ContactMergeCandidateRow>> {
  const r = await apiClient.get<MergeCandidatePage<ContactMergeCandidateRow>>(
    '/admin/contact-merge-candidates',
    { params: pageParams(status, params) },
  );
  return r.data;
}

export async function approveContactMerge(
  candidateId: string,
  notes?: string,
  ids?: { canonical_entity_id: string; duplicate_entity_id?: string | null },
): Promise<MergeActionResult> {
  const r = await apiClient.post<MergeActionResult>(
    `/admin/contact-merge-candidates/${candidateId}/approve`,
    {
      notes: notes || null,
      canonical_entity_id: ids?.canonical_entity_id ?? null,
      duplicate_entity_id: ids?.duplicate_entity_id ?? null,
    },
  );
  return r.data;
}

export async function rejectContactMerge(candidateId: string, notes?: string): Promise<MergeActionResult> {
  const r = await apiClient.post<MergeActionResult>(
    `/admin/contact-merge-candidates/${candidateId}/reject`,
    { notes: notes || null },
  );
  return r.data;
}

export async function listCompanyMergeCandidates(
  status: MergeReviewStatus | 'all' = 'pending',
  params: MergeListParams = {},
): Promise<MergeCandidatePage<CompanyMergeCandidateRow>> {
  const r = await apiClient.get<MergeCandidatePage<CompanyMergeCandidateRow>>(
    '/admin/company-merge-candidates',
    { params: pageParams(status, params) },
  );
  return r.data;
}

export async function approveCompanyMerge(candidateId: string, notes?: string): Promise<MergeActionResult> {
  const r = await apiClient.post<MergeActionResult>(
    `/admin/company-merge-candidates/${candidateId}/approve`,
    { notes: notes || null },
  );
  return r.data;
}

export async function rejectCompanyMerge(candidateId: string, notes?: string): Promise<MergeActionResult> {
  const r = await apiClient.post<MergeActionResult>(
    `/admin/company-merge-candidates/${candidateId}/reject`,
    { notes: notes || null },
  );
  return r.data;
}

export interface CompanyMergeRevertResult {
  applied: boolean;
  error?: string | null;
  entity_id?: string | null;
  was_created?: boolean | null;
  survivor_name?: string | null;
  duplicate_name?: string | null;
  ticker?: string | null;
  forms?: string[];
  removals?: Record<string, { canonical_name: string; aliases_removed: string[] }>;
  codes_to_move?: { entity_id: string; via: string }[];
  would_create?: { name: string; ticker: string | null; domain: string | null } | null;
  moved?: Record<string, number>;
  not_reverted?: string;
  assayer_rows?: { entity_id: string; canonical_identifier: string | null; display_name: string | null }[];
  assayer_repointed?: number;
}

/** Revert a WRONG approved company merge (Cynapse 0.19.2): dry run by default. */
export async function revertCompanyMerge(candidateId: string, params: { dry_run: boolean; duplicate_manages_survivor?: boolean }): Promise<CompanyMergeRevertResult> {
  const r = await apiClient.post<CompanyMergeRevertResult>(
    `/admin/company-merge-candidates/${candidateId}/revert`,
    { dry_run: params.dry_run, duplicate_manages_survivor: !!params.duplicate_manages_survivor },
  );
  return r.data;
}

export async function listFundMergeCandidates(
  status: MergeReviewStatus | 'all' = 'pending',
  params: MergeListParams = {},
): Promise<MergeCandidatePage<FundMergeCandidateRow>> {
  const r = await apiClient.get<MergeCandidatePage<FundMergeCandidateRow>>(
    '/admin/fund-merge-candidates',
    { params: pageParams(status, params) },
  );
  return r.data;
}

export async function approveFundMerge(candidateId: string, notes?: string): Promise<MergeActionResult> {
  const r = await apiClient.post<MergeActionResult>(
    `/admin/fund-merge-candidates/${candidateId}/approve`,
    { notes: notes || null },
  );
  return r.data;
}

export async function rejectFundMerge(candidateId: string, notes?: string): Promise<MergeActionResult> {
  const r = await apiClient.post<MergeActionResult>(
    `/admin/fund-merge-candidates/${candidateId}/reject`,
    { notes: notes || null },
  );
  return r.data;
}

// ===========================================================================
// Attribute-change candidates (FR Phase 2 — entity temporal identity)
// ===========================================================================

export type AttributeChangeReviewStatus =
  | 'pending'
  | 'approved_apply'
  | 'rejected_keep'
  | 'expired'
  | 'superseded';

export interface AttributeChangeCandidateRow {
  candidate_id: string;
  entity_id: string;
  attribute_type: string;
  proposed_value: string;
  proposed_value_norm: string;
  current_value: string | null;
  source: string;
  source_announcement_id: string | null;
  similarity_score: number | null;
  match_method: string | null;
  review_status: AttributeChangeReviewStatus;
  reviewed_by: string | null;
  reviewed_at: string | null;
  review_notes: string | null;
  created_at: string;
}

export interface AttributeChangeActionResult {
  candidate_id: string;
  applied?: boolean;
  rejected?: boolean;
  entity_id?: string;
  prior_value?: string;
  applied_value?: string;
  error?: string;
}

export async function listAttributeChangeCandidates(
  status: AttributeChangeReviewStatus | 'all' = 'pending',
  params: MergeListParams = {},
): Promise<MergeCandidatePage<AttributeChangeCandidateRow>> {
  const r = await apiClient.get<MergeCandidatePage<AttributeChangeCandidateRow>>(
    '/admin/attribute-change-candidates',
    { params: pageParams(status, params) },
  );
  return r.data;
}

export async function approveAttributeChange(candidateId: string, notes?: string): Promise<AttributeChangeActionResult> {
  const r = await apiClient.post<AttributeChangeActionResult>(
    `/admin/attribute-change-candidates/${candidateId}/approve`,
    { notes: notes || null },
  );
  return r.data;
}

export async function rejectAttributeChange(candidateId: string, notes?: string): Promise<AttributeChangeActionResult> {
  const r = await apiClient.post<AttributeChangeActionResult>(
    `/admin/attribute-change-candidates/${candidateId}/reject`,
    { notes: notes || null },
  );
  return r.data;
}


// ===========================================================================
// Identity review groups (2026-09-22) — person rows that may be one human.
// ===========================================================================

export interface IdentityRole {
  company: string;
  company_entity_id: string;
  role: string;
  valid_from: string;
  valid_to: string | null;
}

export interface IdentityPerson {
  entity_id: string;
  name: string;
  first_seen: string | null;
  roles: IdentityRole[];
  role_count: number;
  linked_contact_ids: string[];
}

export interface IdentityContact {
  entity_id: string;
  name: string;
  organisation: string | null;
  title: string | null;
  email_count: number;
  email_domains: string[];
  linked_person_id: string | null;
}

export interface IdentityCandidate {
  candidate_id: string;
  match_method: string;
  person_entity_id: string;
  other_entity_id: string | null;
  reason: string | null;
  similarity: number;
  effect: string;
}

export interface IdentityGroup {
  group_key: string;
  member_count: number;
  persons: IdentityPerson[];
  contacts: IdentityContact[];
  candidates: IdentityCandidate[];
  evidence: {
    witness_contacts: string[];
    shared_email: boolean;
    company_secretary_boards: number;
    same_name_only: boolean;
  };
}

export async function listIdentityGroups(params: MergeListParams = {}): Promise<MergeCandidatePage<IdentityGroup>> {
  const q: Record<string, string | number> = { limit: params.limit ?? 10, offset: params.offset ?? 0 };
  if (params.q && params.q.trim()) q.q = params.q.trim();
  const r = await apiClient.get<MergeCandidatePage<IdentityGroup>>('/admin/identity/review-groups', { params: q });
  return r.data;
}

export async function mergeIdentityGroup(survivorId: string, loserIds: string[]) {
  const r = await apiClient.post('/admin/identity/groups/merge', { survivor_id: survivorId, loser_ids: loserIds });
  return r.data as { merged: boolean; survivor_id: string; merged_ids?: string[]; contacts_linked: number; candidates_closed: number };
}

export async function separateIdentity(entityId: string, others: string[]) {
  const r = await apiClient.post('/admin/identity/groups/separate', { entity_id: entityId, others });
  return r.data as { candidates_rejected: number };
}

export async function unlinkIdentityContact(contactId: string, personId: string) {
  const r = await apiClient.post('/admin/identity/contacts/unlink', { contact_entity_id: contactId, person_entity_id: personId });
  return r.data as { ended: boolean };
}
